import { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useOnboardingTour, TourStep } from '@/hooks/useOnboardingTour';
import { X, ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';
import { useConfetti } from '@/hooks/useConfetti';

interface TooltipPosition {
  top: number;
  left: number;
  arrowPosition: 'top' | 'bottom' | 'left' | 'right';
}

function calculateTooltipPosition(
  targetRect: DOMRect,
  placement: TourStep['placement'],
  tooltipWidth: number = 300,
  tooltipHeight: number = 180
): TooltipPosition {
  const padding = 12;
  const arrowSize = 8;
  
  switch (placement) {
    case 'bottom':
      return {
        top: targetRect.bottom + padding + arrowSize,
        left: Math.max(padding, Math.min(
          targetRect.left + (targetRect.width / 2) - (tooltipWidth / 2),
          window.innerWidth - tooltipWidth - padding
        )),
        arrowPosition: 'top'
      };
    case 'top':
      return {
        top: targetRect.top - tooltipHeight - padding - arrowSize,
        left: Math.max(padding, Math.min(
          targetRect.left + (targetRect.width / 2) - (tooltipWidth / 2),
          window.innerWidth - tooltipWidth - padding
        )),
        arrowPosition: 'bottom'
      };
    case 'left':
      return {
        top: Math.max(padding, targetRect.top + (targetRect.height / 2) - (tooltipHeight / 2)),
        left: targetRect.left - tooltipWidth - padding - arrowSize,
        arrowPosition: 'right'
      };
    case 'right':
      return {
        top: Math.max(padding, targetRect.top + (targetRect.height / 2) - (tooltipHeight / 2)),
        left: targetRect.right + padding + arrowSize,
        arrowPosition: 'left'
      };
    default:
      return { top: 100, left: 100, arrowPosition: 'top' };
  }
}

export function OnboardingTour() {
  const {
    isActive,
    currentStep,
    currentStepData,
    totalSteps,
    nextStep,
    prevStep,
    skipTour,
    endTour
  } = useOnboardingTour();

  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<TooltipPosition | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const { fire } = useConfetti();

  const handleNextStep = () => {
    if (currentStep === totalSteps - 1) {
      // Fire celebration confetti on tour completion
      fire('celebration');
    }
    nextStep();
  };

  useEffect(() => {
    if (!isActive || !currentStepData) return;

    const findTarget = () => {
      const target = document.querySelector(currentStepData.target);
      if (target) {
        const rect = target.getBoundingClientRect();
        setTargetRect(rect);
        
        const position = calculateTooltipPosition(rect, currentStepData.placement);
        setTooltipPosition(position);
        
        // Scroll target into view if needed
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else {
        // Target not found, skip to next step or end tour
        console.warn(`Tour target not found: ${currentStepData.target}`);
        if (currentStep < totalSteps - 1) {
          nextStep();
        } else {
          endTour();
        }
      }
    };

    // Small delay to allow DOM to render
    const timer = setTimeout(findTarget, 100);
    
    // Update position on resize
    window.addEventListener('resize', findTarget);
    
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', findTarget);
    };
  }, [isActive, currentStep, currentStepData, nextStep, endTour, totalSteps]);

  if (!isActive || !currentStepData || !targetRect || !tooltipPosition) {
    return null;
  }

  const overlay = (
    <div className="fixed inset-0 z-[9998]">
      {/* Dark overlay with spotlight cutout */}
      <svg className="absolute inset-0 w-full h-full">
        <defs>
          <mask id="spotlight-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            <rect
              x={targetRect.left - 4}
              y={targetRect.top - 4}
              width={targetRect.width + 8}
              height={targetRect.height + 8}
              rx="8"
              fill="black"
            />
          </mask>
        </defs>
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill="rgba(0, 0, 0, 0.7)"
          mask="url(#spotlight-mask)"
        />
      </svg>

      {/* Spotlight border glow */}
      <div
        className="absolute rounded-lg border-2 border-teen-cyan shadow-[0_0_20px_rgba(34,211,238,0.5)] animate-pulse"
        style={{
          top: targetRect.top - 4,
          left: targetRect.left - 4,
          width: targetRect.width + 8,
          height: targetRect.height + 8,
          pointerEvents: 'none'
        }}
      />

      {/* Tooltip */}
      <Card
        ref={tooltipRef}
        className="absolute z-[9999] w-[300px] bg-card border-teen-cyan/30 shadow-xl animate-fade-in"
        style={{
          top: tooltipPosition.top,
          left: tooltipPosition.left
        }}
      >
        {/* Arrow */}
        <div
          className={`absolute w-3 h-3 bg-card border-teen-cyan/30 transform rotate-45 ${
            tooltipPosition.arrowPosition === 'top' 
              ? '-top-1.5 left-1/2 -translate-x-1/2 border-l border-t' 
              : tooltipPosition.arrowPosition === 'bottom'
              ? '-bottom-1.5 left-1/2 -translate-x-1/2 border-r border-b'
              : tooltipPosition.arrowPosition === 'left'
              ? '-left-1.5 top-1/2 -translate-y-1/2 border-l border-b'
              : '-right-1.5 top-1/2 -translate-y-1/2 border-r border-t'
          }`}
        />
        
        <CardContent className="p-4">
          {/* Header */}
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-lg bg-gradient-to-r from-teen-cyan to-teen-purple bg-clip-text text-transparent flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-teen-cyan" />
              {currentStepData.title}
            </h3>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={skipTour}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Content */}
          <p className="text-sm text-muted-foreground mb-4">
            {currentStepData.content}
          </p>

          {/* Footer */}
          <div className="flex items-center justify-between">
            {/* Progress dots */}
            <div className="flex gap-1">
              {Array.from({ length: totalSteps }).map((_, i) => (
                <div
                  key={i}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    i === currentStep 
                      ? 'bg-teen-cyan' 
                      : i < currentStep 
                      ? 'bg-teen-green' 
                      : 'bg-muted'
                  }`}
                />
              ))}
            </div>

            {/* Navigation */}
            <div className="flex gap-2">
              {currentStep > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={prevStep}
                  className="text-xs"
                >
                  <ChevronLeft className="h-3 w-3 mr-1" />
                  Back
                </Button>
              )}
              <Button
                size="sm"
                onClick={handleNextStep}
                className="bg-teen-cyan hover:bg-teen-cyan/90 text-white text-xs"
              >
                {currentStep === totalSteps - 1 ? (
                  <>
                    <Sparkles className="h-3 w-3 mr-1" />
                    Finish
                  </>
                ) : (
                  <>
                    Next
                    <ChevronRight className="h-3 w-3 ml-1" />
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  return createPortal(overlay, document.body);
}
